<template>
  <v-container fluid class="pa-4">
    <v-card class="elevation-2">
      <v-toolbar color="green darken-2" dark dense>
        <v-icon left>point_of_sale</v-icon>
        <v-toolbar-title>Diğer Satışlar</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon :loading="loading" @click="loadAll">
          <v-icon>refresh</v-icon>
        </v-btn>
        <v-btn color="white" flat class="inv-btn ma-0 ml-2" @click="openSell">
          <v-icon left small>point_of_sale</v-icon>
          Satış Yap
        </v-btn>
      </v-toolbar>

      <v-progress-linear v-if="loading" indeterminate color="green darken-2" class="ma-0"></v-progress-linear>

      <v-card-text>
        <div class="inv-hint">
          Sipariş dışı manuel satış. Envanterden düşer; iade edilince stok geri eklenir.
        </div>

        <v-layout wrap align-center class="mb-3">
          <v-flex xs12 sm6 md4 class="pr-2">
            <v-text-field
              v-model="search"
              label="Ara (ürün / seri / açıklama)"
              prepend-icon="search"
              single-line
              hide-details
              clearable
            ></v-text-field>
          </v-flex>
        </v-layout>

        <v-layout wrap class="inv-stats">
          <v-flex xs6 sm3 class="pr-2">
            <div class="inv-stat inv-stat--grey">
              <div class="inv-stat-label">Kayıt</div>
              <div class="inv-stat-value">{{ filteredLines.length }}</div>
            </div>
          </v-flex>
          <v-flex xs6 sm3 class="pr-2">
            <div class="inv-stat inv-stat--green">
              <div class="inv-stat-label">Aktif Satış</div>
              <div class="inv-stat-value">{{ activeCount }}</div>
            </div>
          </v-flex>
          <v-flex xs6 sm3 class="pr-2">
            <div class="inv-stat inv-stat--teal">
              <div class="inv-stat-label">Aktif satış toplamı</div>
              <div class="inv-stat-value">{{ formatMoney(activeTotal) }} <span class="inv-stat-unit">₺</span></div>
            </div>
          </v-flex>
          <v-flex xs6 sm3>
            <div class="inv-stat inv-stat--red">
              <div class="inv-stat-label">İade Edilen</div>
              <div class="inv-stat-value">{{ refundedCount }}</div>
            </div>
          </v-flex>
        </v-layout>

        <div v-if="!filteredLines.length && !loading" class="inv-empty">
          <v-icon large color="grey lighten-1">point_of_sale</v-icon>
          <div>Henüz manuel satış yok.</div>
        </div>
        <div v-else>
          <div class="inv-card" :class="lineCardClass(line)" v-for="line in filteredLines" :key="line.id">
            <div class="inv-icon" :class="typeIconClass(line.inventoryType)">
              <v-icon color="white" small>{{ typeIcon(line.inventoryType) }}</v-icon>
            </div>

            <div class="inv-card-l">
              <div class="inv-head">
                <span class="inv-title">{{ line.productName }}{{ variantSuffix(line) }}</span>
                <span class="inv-chip" :class="typeChipClass(line.inventoryType)">{{ line.inventoryTypeName }}</span>
                <span class="inv-chip ml-2" :class="line.status === 0 ? 'inv-chip--done' : 'inv-chip--red'">
                  {{ line.statusName }}
                </span>
              </div>

              <div class="inv-items">
                <span class="inv-item">× {{ line.quantity }} adet</span>
                <span v-if="line.serialNumber" class="inv-item">Seri: {{ line.serialNumber }}</span>
              </div>

              <div v-if="line.description" class="inv-note">“{{ line.description }}”</div>

              <div class="inv-meta">
                <v-icon x-small color="blue-grey lighten-2">event</v-icon>
                {{ formatDate(line.saleDate) }}
              </div>
            </div>

            <div class="inv-card-r">
              <div class="inv-value">
                <div class="inv-value-num">{{ formatMoney(line.amount) }}</div>
                <div class="inv-value-label">TL</div>
              </div>
              <v-btn
                v-if="line.status === 0"
                color="red darken-1"
                dark
                class="inv-btn ma-0 ml-3"
                @click="openRefund(line)"
              >
                <v-icon small left>undo</v-icon>
                İade
              </v-btn>
            </div>
          </div>
        </div>
      </v-card-text>
    </v-card>

    <!-- ========== SATIŞ YAP DIALOG ========== -->
    <v-dialog v-model="sellDialog" max-width="560" persistent>
      <v-card>
        <v-card-title class="headline green darken-2 white--text">
          <v-icon left color="white">point_of_sale</v-icon>
          Manuel Satış
        </v-card-title>
        <v-card-text class="pa-4">
          <v-text-field v-model="sellForm.amount" label="Tutar (₺)" placeholder="Örn. 12500" prepend-icon="payments"></v-text-field>
          <v-textarea v-model="sellForm.description" label="Açıklama" prepend-icon="notes" rows="2" auto-grow></v-textarea>

          <v-tabs v-model="sellTab" grow class="mb-2">
            <v-tab>Ziynet / Külçe / Diğer</v-tab>
            <v-tab>Takı</v-tab>
          </v-tabs>

          <div v-if="sellTab === 0">
            <v-select
              v-model="sellForm.inventoryItemId"
              :items="adetItems"
              item-text="label"
              item-value="id"
              label="Ürün"
              prepend-icon="category"
            ></v-select>
            <div v-if="selectedInventoryItem" class="caption grey--text mb-2">
              Ana envanterde boşta: <strong>{{ availableOf(selectedInventoryItem) }}</strong> adet
            </div>
            <v-text-field v-model.number="sellForm.quantity" label="Adet" type="number" min="1" prepend-icon="plus_one"></v-text-field>
          </div>

          <div v-else>
            <v-text-field
              v-model="sellForm.barcode"
              label="Takı Barkodu / Seri No"
              prepend-icon="qr_code_scanner"
              hint="Sertifikadaki barkodu okutun veya yazın"
              persistent-hint
              @keyup.enter="saveSell"
            ></v-text-field>
          </div>
        </v-card-text>
        <v-card-actions class="pa-3">
          <v-spacer></v-spacer>
          <v-btn flat color="grey" @click="sellDialog = false">İptal</v-btn>
          <v-btn color="green darken-2" dark class="inv-btn" :loading="sellSaving" @click="saveSell">Satış Yap</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ========== İADE DIALOG (adet) ========== -->
    <v-dialog v-model="refundDialog" max-width="460" persistent>
      <v-card>
        <v-card-title class="headline red darken-1 white--text">
          <v-icon left color="white">undo</v-icon>
          Satışı İade Et
        </v-card-title>
        <v-card-text class="pa-4">
          <div class="mb-2">{{ refundTarget && refundTarget.productName }}{{ variantSuffix(refundTarget) }}</div>
          <v-text-field
            v-model.number="refundQuantity"
            label="İade edilecek adet"
            type="number"
            min="1"
            prepend-icon="plus_one"
            :max="refundTarget ? refundTarget.quantity : 1"
          ></v-text-field>
          <div class="caption grey--text">Satılan: {{ refundTarget ? refundTarget.quantity : 0 }} adet</div>
        </v-card-text>
        <v-card-actions class="pa-3">
          <v-spacer></v-spacer>
          <v-btn flat color="grey" @click="refundDialog = false">İptal</v-btn>
          <v-btn color="red darken-1" dark class="inv-btn" :loading="refundSaving" @click="confirmRefund">İade Et</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
export default {
  name: "DigerSatislar",
  data: () => ({
    loading: false,
    lines: [],
    inventoryItems: [],
    zimmetLines: [],
    search: "",
    headers: [
      { text: "Tip", value: "inventoryTypeName" },
      { text: "Ürün", value: "productName" },
      { text: "Seri No", value: "serialNumber" },
      { text: "Adet", value: "quantity", align: "right" },
      { text: "Tutar", value: "amount", align: "right" },
      { text: "Tarih", value: "saleDate" },
      { text: "Açıklama", value: "description" },
      { text: "Durum", value: "status" },
      { text: "", value: "actions", sortable: false },
    ],

    sellDialog: false,
    sellSaving: false,
    sellTab: 0,
    sellForm: { amount: "", description: "", inventoryItemId: null, quantity: 1, barcode: "" },

    refundDialog: false,
    refundSaving: false,
    refundTarget: null,
    refundQuantity: 1,
  }),

  computed: {
    adetItems() {
      return this.inventoryItems
        .filter((i) => i.inventoryType !== 0)
        .map((i) => ({
          ...i,
          label: `${i.inventoryTypeName} - ${i.productName}${this.variantSuffix(i)} (${i.weight} gr, ${i.purityLabel}) — boşta ${this.availableOf(i)}`,
        }));
    },
    selectedInventoryItem() {
      return this.inventoryItems.find((i) => i.id === this.sellForm.inventoryItemId) || null;
    },
    activeTotal() {
      return this.lines.filter((l) => l.status === 0).reduce((s, l) => s + (Number(l.amount) || 0), 0);
    },
    filteredLines() {
      const q = (this.search || "").toLocaleLowerCase("tr");
      if (!q) return this.lines;
      return this.lines.filter((l) =>
        [l.productName, l.serialNumber, l.inventoryTypeName, l.description]
          .filter(Boolean)
          .some((v) => v.toLocaleLowerCase("tr").includes(q))
      );
    },
    activeCount() {
      return this.filteredLines.filter((l) => l.status === 0).length;
    },
    refundedCount() {
      return this.filteredLines.filter((l) => l.status !== 0).length;
    },
  },

  created() {
    this.loadAll();
  },

  methods: {
    variantSuffix(i) {
      if (!i) return "";
      const parts = [i.kulp, i.dateType].filter(Boolean);
      return parts.length ? ` [${parts.join(" / ")}]` : "";
    },
    typeColor(t) {
      if (t === 0) return "teal";
      if (t === 1) return "amber darken-2";
      return "blue-grey darken-1";
    },
    typeIcon(t) {
      if (t === 0) return "diamond";
      if (t === 1) return "monetization_on";
      if (t === 2) return "view_in_ar";
      return "category";
    },
    typeIconClass(t) {
      if (t === 0) return "inv-icon--teal";
      if (t === 1) return "inv-icon--amber";
      if (t === 2) return "inv-icon--brown";
      return "inv-icon--grey";
    },
    typeChipClass(t) {
      if (t === 0) return "inv-chip--teal";
      if (t === 1) return "inv-chip--brown";
      if (t === 2) return "inv-chip--purple";
      return "inv-chip--grey";
    },
    lineCardClass(line) {
      if (!line) return "";
      return line.status === 0 ? "inv-card--done" : "inv-card--red";
    },
    formatMoney(v) {
      if (v == null) return "0,00";
      return Number(v).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    formatDate(d) {
      if (!d) return "-";
      return new Date(d).toLocaleDateString("tr-TR");
    },
    parseNum(s) {
      if (s == null || s === "") return null;
      const n = parseFloat(String(s).trim().replace(/\./g, "").replace(",", "."));
      return Number.isNaN(n) ? null : n;
    },
    availableOf(item) {
      if (!item) return 0;
      const assigned = this.zimmetLines
        .filter((l) => l.inventoryItemId === item.id)
        .reduce((s, l) => s + (l.quantity || 0), 0);
      return item.quantity - assigned;
    },

    async loadAll() {
      this.loading = true;
      try {
        const [linesRes, itemsRes, zimmetRes] = await Promise.all([
          this.$store.dispatch("inventory/getOtherSales"),
          this.$store.dispatch("inventory/getInventoryItems"),
          this.$store.dispatch("inventory/getAllZimmets"),
        ]);
        this.lines = linesRes || [];
        this.inventoryItems = itemsRes || [];
        this.zimmetLines = zimmetRes || [];
      } catch (e) {
        this.notify("Satış verileri yüklenemedi: " + this.errMsg(e));
      } finally {
        this.loading = false;
      }
    },

    openSell() {
      this.sellForm = { amount: "", description: "", inventoryItemId: null, quantity: 1, barcode: "" };
      this.sellTab = 0;
      this.sellDialog = true;
    },

    async saveSell() {
      const amount = this.parseNum(this.sellForm.amount);
      if (amount == null || amount < 0) {
        this.notify("Geçerli bir tutar girin.");
        return;
      }
      let dto;
      if (this.sellTab === 1) {
        const code = (this.sellForm.barcode || "").trim().replace(/\*/g, "-"); // okuyucu "-"yi "*" gönderebilir
        if (!code) { this.notify("Takı barkodu okutun."); return; }
        dto = { amount, description: this.sellForm.description, barcode: code };
      } else {
        if (!this.sellForm.inventoryItemId) { this.notify("Ürün seçin."); return; }
        if (!this.sellForm.quantity || this.sellForm.quantity <= 0) { this.notify("Adet 0'dan büyük olmalı."); return; }
        dto = {
          amount,
          description: this.sellForm.description,
          inventoryItemId: this.sellForm.inventoryItemId,
          quantity: this.sellForm.quantity,
        };
      }
      this.sellSaving = true;
      try {
        const res = await this.$store.dispatch("inventory/otherSaleSell", dto);
        if (res && res.success === false) { this.notify(res.message); return; }
        this.sellDialog = false;
        this.notify((res && res.message) || "Satış kaydedildi.");
        await this.loadAll();
      } catch (e) {
        this.notify("Hata: " + this.errMsg(e));
      } finally {
        this.sellSaving = false;
      }
    },

    openRefund(line) {
      this.refundTarget = line;
      if (line.inventoryType === 0) {
        this.doRefund(line, 0);
        return;
      }
      this.refundQuantity = line.quantity;
      this.refundDialog = true;
    },

    async confirmRefund() {
      if (!this.refundTarget) return;
      if (!this.refundQuantity || this.refundQuantity <= 0) { this.notify("Adet 0'dan büyük olmalı."); return; }
      await this.doRefund(this.refundTarget, this.refundQuantity);
      this.refundDialog = false;
    },

    async doRefund(line, quantity) {
      this.refundSaving = true;
      try {
        const res = await this.$store.dispatch("inventory/otherSaleRefund", { saleId: line.id, quantity });
        if (res && res.success === false) { this.notify(res.message); return; }
        this.notify((res && res.message) || "İade edildi.");
        await this.loadAll();
      } catch (e) {
        this.notify("Hata: " + this.errMsg(e));
      } finally {
        this.refundSaving = false;
      }
    },

    errMsg(e) {
      return (e && e.response && e.response.data && e.response.data.message) || (e && e.message) || "Bilinmeyen hata";
    },
    notify(msg) {
      this.$store.state.dialog = true;
      this.$store.state.message = msg;
    },
  },
};
</script>
